/**
 * La fenêtre de commande du frais, découpée jour par jour — règle pure.
 *
 * « Les fruits, légumes et produits frais ne sont disponibles que le mercredi
 * soir, le jeudi, le vendredi et le samedi » (Philippe, 28/09/2026) : à dessiner,
 * pas à écrire. La fenêtre vit dans les réglages (`ecommerce.ordering` :
 * jour/heure d'ouverture et de fermeture, heure belge) ; on la projette sur une
 * semaine commençant le LUNDI, une case par jour, pour que le dessin suive
 * tout changement d'horaire.
 */

export interface FenetreHebdo {
  enabled?: boolean;
  /** 0 = dimanche … 6 = samedi */
  open_weekday?: number | null;
  open_hour?: number | null;
  close_weekday?: number | null;
  close_hour?: number | null;
}

export interface CaseJour {
  /** Jour de la semaine (0 = dimanche … 6 = samedi). */
  jour: number;
  /** Portion ouverte de la journée, en fractions 0..1 (debut = fin = 0 : fermé). */
  debut: number;
  fin: number;
}

export interface FenetreDessinee {
  /** Lundi → dimanche. */
  cases: CaseJour[];
  /** Ouverture et fermeture en fraction de la semaine (lundi 0 h = 0). */
  debut: number;
  fin: number;
  ouverture: { jour: number; heure: number };
  fermeture: { jour: number; heure: number };
}

const MIN_JOUR = 1440;
const MIN_SEMAINE = 7 * MIN_JOUR;
/** Rang dans une semaine du lundi : lundi = 0 … dimanche = 6. */
export const rangLundi = (j: number) => (j + 6) % 7;
/** Jours dans l'ordre lundi → dimanche. */
export const JOURS_LUNDI = [1, 2, 3, 4, 5, 6, 0];

export function fenetreDessinee(f: FenetreHebdo | null | undefined): FenetreDessinee | null {
  if (!f || f.enabled === false) return null;
  const { open_weekday: ow, open_hour: oh, close_weekday: cw, close_hour: ch } = f;
  if (ow == null || cw == null) return null;
  const o = rangLundi(ow) * MIN_JOUR + (Number(oh) || 0) * 60;
  const c = rangLundi(cw) * MIN_JOUR + (Number(ch) || 0) * 60;
  if (o === c) return null;
  // Une fenêtre qui enjambe la fin de semaine (samedi → mardi) se coupe en deux.
  const plages: Array<[number, number]> = c > o ? [[o, c]] : [[o, MIN_SEMAINE], [0, c]];

  const cases = JOURS_LUNDI.map((jour, r) => {
    const a = r * MIN_JOUR;
    const b = a + MIN_JOUR;
    let debut = 0;
    let fin = 0;
    for (const [p, q] of plages) {
      const d = Math.max(a, p);
      const e = Math.min(b, q);
      if (e > d) {
        debut = fin === 0 ? (d - a) / MIN_JOUR : Math.min(debut, (d - a) / MIN_JOUR);
        fin = Math.max(fin, (e - a) / MIN_JOUR);
      }
    }
    return { jour, debut, fin };
  });

  return {
    cases,
    debut: o / MIN_SEMAINE,
    fin: c / MIN_SEMAINE,
    ouverture: { jour: ow, heure: Number(oh) || 0 },
    fermeture: { jour: cw, heure: Number(ch) || 0 },
  };
}

/** Position de « maintenant » dans la semaine (lundi 0 h = 0), en heure BELGE. */
export function maintenantDansLaSemaine(at: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Brussels',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(at);
  const v = (t: string) => parts.find((p) => p.type === t)?.value ?? '0';
  const jour = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(v('weekday'));
  const heure = Number(v('hour')) % 24;
  return (rangLundi(jour) * MIN_JOUR + heure * 60 + Number(v('minute'))) / MIN_SEMAINE;
}
